import type { ParagraphBenefitsTools as ParagraphBenefitsToolsData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphBenefitsTools.module.css";

/**
 * Layout 64 - `PARAGRAPH_BENEFITS_TOOLS_LAYOUT`: "monster" 2-sekcyjny.
 *
 * Sekcja gorna (2-kol split, jasna):
 *   - Lewa: eyebrow (text_1) + H2 (name) + lead (text)
 *   - Prawa: metric-cards (metrics[] {pill, name, desc}) - do 4 kart
 *
 * Sekcja dolna (dark full-bleed card):
 *   - dark_title (H3) + dark_intro
 *   - grid 3-kol tools (tools[] {badge, name, desc}) - do 6 kart
 *   - footer_note (mala linia pod grid'em)
 */
export function ParagraphBenefitsTools({
  paragraph,
}: {
  paragraph: ParagraphBenefitsToolsData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: eyebrow,
    name: title,
    text: lead,
    metrics,
    dark_title: darkTitle,
    dark_intro: darkIntro,
    tools,
    footer_note: footerNote,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasMetrics = Array.isArray(metrics) && metrics.length > 0;
  const hasDarkIntro = darkIntro && darkIntro.trim() !== "";
  const hasTools = Array.isArray(tools) && tools.length > 0;
  const hasFooter = footerNote && footerNote.trim() !== "";
  const hasDarkSection = Boolean(darkTitle || hasDarkIntro || hasTools || hasFooter);

  return (
    <section className={styles.section}>
      {/* ─── Sekcja gorna ─────────────────────────────────── */}
      <div className={styles.top}>
        <div className={styles.topLeft}>
          {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          {title ? <h2 className={styles.title}>{title}</h2> : null}
          {hasLead ? <p className={styles.lead}>{lead}</p> : null}
        </div>

        {hasMetrics ? (
          <ul className={styles.metrics}>
            {metrics.map((m, idx) => (
              <li key={idx} className={styles.metricCard}>
                {m.pill ? <span className={styles.metricPill}>{m.pill}</span> : null}
                {m.name || m.desc ? (
                  <div className={styles.metricBody}>
                    {m.name ? <h3 className={styles.metricName}>{m.name}</h3> : null}
                    {m.desc ? <p className={styles.metricDesc}>{m.desc}</p> : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* ─── Sekcja dolna (dark) ──────────────────────────── */}
      {hasDarkSection ? (
        <div className={styles.darkWrap}>
          <div className={styles.dark}>
            <div className={styles.darkHead}>
              {darkTitle ? <h3 className={styles.darkTitle}>{darkTitle}</h3> : null}
              {hasDarkIntro ? <p className={styles.darkIntro}>{darkIntro}</p> : null}
            </div>

            {hasTools ? (
              <ul className={styles.tools}>
                {tools.map((tool, idx) => (
                  <li key={idx} className={styles.toolCard}>
                    {tool.badge ? <span className={styles.toolBadge}>{tool.badge}</span> : null}
                    {tool.name ? <h4 className={styles.toolName}>{tool.name}</h4> : null}
                    {tool.desc ? <p className={styles.toolDesc}>{tool.desc}</p> : null}
                  </li>
                ))}
              </ul>
            ) : null}

            {hasFooter ? <p className={styles.footerNote}>{footerNote}</p> : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
